import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { ContadorService } from '../../../services/contador/contador.service';
import { MOEDA_CONFIG, MOEDA_OPTIONS, Moeda as MoedaEnum, MoedaConfig } from '../../../enums/moeda.enum';
import {
  ClienteDisponivelContratoDTO,
  ContaExistenteContratoDTO,
  ContratoContadorConsultaDTO,
  ContratoContadorRelatorioFiltroDTO,
  CupomContadorConsultaDTO,
  MesReferencia,
  MinhaSituacaoContadorDTO,
  MoedaContrato,
  RankingContadorConsultaDTO,
  ResumoContadorConsultaDTO,
  SituacaoContaExistente,
  SolicitarContratoContadorDTO,
  StatusContratoContador,
} from '../../../models/contador/contador.model';
import {
  GerarParcelasLoteDTO,
  GerarParcelasLoteResultadoDTO,
} from '../../../models/contador/parcela-contrato.model';
import {
  DespesaAcessoriaContadorDTO,
  DespesaAcessoriaContadorFiltroDTO,
  DespesaAcessoriaRelatorioPdfFiltroDTO,
  StatusDespesaAcessoria,
} from '../../../models/contador/despesa-acessoria.model';
import { DespesaAcessoriaService } from '../../../services/contador/despesa-acessoria.service';
import { PeriodoFiltro } from '../../../models/dashboard/periodo-filtro.model';
import { FileUtils } from '../../../shared/utils/file-utils';
import { PaisDDI } from '../../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../../shared/data/paises-ddi.data';
import { filtrarRucDoEvento } from '../../../shared/utils/ruc.util';
import { isEmailValido } from '../../../shared/utils/email.util';
import {
  AtividadeBackend,
  AtividadeEmpresaService,
} from '../../../services/atividade-empresa.service';
import { CidadeService } from '../../../services/configuracoes/cidade.service';
import { CidadeDTO, CidadeResponseDTO } from '../../../models/configuracoes/cidade.model';

/** Opcao do dropdown "Cidade": guarda o estado para exibir "Cidade - Estado" (igual ao /signup). */
interface CidadeOption {
  label: string;
  value: number;
  nomeEstado: string;
}

/**
 * Situacoes que o aviso de "conta ja existente" sabe desenhar. Alem das 3 do backend
 * (`SituacaoContaExistente`), a tela tem DESCONHECIDA para degradar com seguranca quando
 * o backend mandar um valor que esta versao ainda nao conhece.
 */
type SituacaoContaExibida = SituacaoContaExistente | 'DESCONHECIDA';

/**
 * Resultado de UMA checagem de conta por email, com a origem preservada.
 *
 * ⚠️ Existe para nao repetir o erro do `catchError(() => of([]))`: lista vazia por FALHA
 * (rede/403/500) e lista vazia por RESPOSTA 200 sao coisas diferentes. Enquanto o vazio so
 * escondia um aviso, confundir as duas era inofensivo; agora o vazio MARCA o "criar cadastro"
 * (que cria uma empresa), entao a auto-marcacao exige `consultou && !erro`.
 */
interface ResultadoBuscaEmail {
  /** Email consultado (ja normalizado), para casar a resposta com o valor atual do campo. */
  email: string;
  /** Contas devolvidas pelo backend (no maximo 1). */
  contas: ContaExistenteContratoDTO[];
  /** A chamada falhou: NAO se pode concluir nada sobre a existencia da conta. */
  erro: boolean;
  /** O backend chegou a ser consultado (false quando o email nem e sintaticamente completo). */
  consultou: boolean;
}

/**
 * Area exclusiva do Contador Parceiro.
 *
 * Dashboard read-only que decide o conteudo pela situacao do usuario
 * (`minha-situacao`):
 * - nao-contador  -> painel de solicitar (`app-solicitar-contador-panel`);
 * - contador      -> resumo + ranking + tabela paginada (lazy) de contratos.
 *
 * Cada bloco do dashboard carrega de forma independente, com seus proprios
 * estados de loading/erro/vazio (a falha de um nao derruba os outros).
 *
 * Fonte da verdade: `vendas/docs/api/API_CONTRACT_contador-dashboard.md`.
 */
@Component({
  selector: 'app-area-contador',
  templateUrl: './area-contador.component.html',
  styleUrls: ['./area-contador.component.scss'],
  providers: [MessageService],
})
export class AreaContadorComponent implements OnInit {
  // ===========================================================================
  // Situacao / branch
  // ===========================================================================

  situacao: MinhaSituacaoContadorDTO | null = null;
  carregandoSituacao = true;
  erroSituacao = false;

  // ===========================================================================
  // Resumo
  // ===========================================================================

  resumo: ResumoContadorConsultaDTO | null = null;
  carregandoResumo = false;
  erroResumo = false;

  // ===========================================================================
  // Ranking
  // ===========================================================================

  ranking: RankingContadorConsultaDTO | null = null;
  carregandoRanking = false;
  erroRanking = false;

  // ===========================================================================
  // Tabela de contratos (lazy)
  // ===========================================================================

  contratos: ContratoContadorConsultaDTO[] = [];
  first = 0;
  rows = 10;
  totalRecords = 0;
  carregandoContratos = true; // true ate o primeiro lazy-load resolver
  erroContratos = false;

  /** Opcoes do dropdown "itens por pagina" do footer. */
  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  /** Ultimo valor de "itens por pagina" ja carregado; usado para descartar o
   *  (ngModelChange) que o dropdown dispara sem o usuario mudar a selecao. */
  private _lastRows = 10;

  // ---- Filtros da aba "Meus contratos" (Estado + periodo de vencimento) ----

  /** Filtro de situacao do contrato; null = todas as situacoes. */
  contratosStatus: StatusContratoContador | null = null;
  /** Intervalo (ISO yyyy-MM-dd) do filtro de periodo de vencimento; undefined = sem filtro. */
  contratosDe: string | undefined = undefined;
  contratosAte: string | undefined = undefined;

  /** Opcoes ({ label, value }) do dropdown de situacao; primeira ("Todos") = null. */
  contratosStatusOptions: { label: string; value: StatusContratoContador | null }[] = [];

  /** Loading/anti-clique-duplo do botao "Exportar" da aba de contratos (compartilhado
   *  pelos 3 formatos — PDF, XLSX, CSV — pois so ha um export por vez). */
  exportandoContratos = false;

  /** Itens do menu popup "Exportar" (PDF / Excel .xlsx / CSV) da aba de contratos.
   *  Montado on-demand na abertura do menu (`abrirMenuExportarContratos`) para
   *  refletir o idioma atual. */
  exportarContratosItens: MenuItem[] = [];

  // ===========================================================================
  // Aba "Perto do vencimento" (contratos ATIVOS perto do vencimento + gerar lote)
  // ===========================================================================

  /** Contratos ATIVOS perto do vencimento (Page.content, tabela lazy). */
  proximos: ContratoContadorConsultaDTO[] = [];
  proximosFirst = 0;
  proximosRows = 10;
  proximosTotalRecords = 0;
  carregandoProximos = false;
  erroProximos = false;
  /** Guard do dropdown "itens por pagina" da tabela de proximos. */
  private _proximosLastRows = 10;

  /** Intervalo (ISO yyyy-MM-dd) do filtro de periodo de vencimento; undefined = sem filtro. */
  proximosDe: string | undefined = undefined;
  proximosAte: string | undefined = undefined;

  /** Contratos marcados na tabela (seleção múltipla para gerar parcelas em lote). */
  proximosSelecionados: ContratoContadorConsultaDTO[] = [];

  /** Marca se a aba de proximos ja carregou (evita recarga redundante ao trocar de aba). */
  private proximosCarregouUmaVez = false;

  // Dialog "Gerar parcelas em lote" (quantidade + data do primeiro vencimento).
  // A periodicidade (parcelas por mês) vem de cada contrato (parcelasPorMes).
  gerarLoteDialogVisivel = false;
  gerarLoteQuantidade: number | null = 1;
  gerarLotePrimeiroVencimento: Date | null = null;
  gerandoLote = false;

  /**
   * Acima deste total (quantidade x contratos selecionados) o sistema pede
   * confirmacao explicita antes de gerar. Como NAO existe teto de quantidade
   * (decisao do Rafael em 30/07/2026), esta confirmacao passou a ser a unica
   * defesa contra erro de digitacao — inclusive com 1 contrato selecionado.
   */
  readonly gerarLoteTotalConfirmacao = 50;

  /** Mensagem inline de erro da quantidade (string vazia = sem erro). */
  gerarLoteQuantidadeErro = '';

  /** Dialog de confirmacao de volume alto (ver gerarLoteTotalConfirmacao). */
  gerarLoteConfirmVolumeVisivel = false;

  // ===========================================================================
  // Aba "Gastos acessorios" (consolidado de TODOS os contratos do contador)
  // ===========================================================================

  /** Pagina atual das despesas (Page.content) — paginacao server-side. */
  despesas: DespesaAcessoriaContadorDTO[] = [];
  despesasFirst = 0;
  despesasRows = 10;
  despesasTotalRecords = 0;
  carregandoDespesas = false;
  erroDespesas = false;
  /** Guard do dropdown "itens por pagina" da tabela de despesas. */
  private _despesasLastRows = 10;

  /** Filtros: indicado (empresa cliente), situacao e periodo por dataPagamento. */
  despesasClienteEmpresaId: number | null = null;
  despesasStatus: StatusDespesaAcessoria | null = null;
  despesasDe: string | undefined = undefined;
  despesasAte: string | undefined = undefined;

  /** Opcoes ({ label, value }) dos dropdowns de filtro; rotulos no idioma atual. */
  despesasIndicadoOptions: { label: string; value: number | null }[] = [];
  despesasStatusOptions: { label: string; value: StatusDespesaAcessoria | null }[] = [];

  /** Marca se a aba de despesas ja carregou (evita recarga ao trocar de aba). */
  private despesasCarregouUmaVez = false;

  /** Loading/anti-clique-duplo do botao "Exportar" da aba de gastos (compartilhado
   *  pelos 3 formatos — PDF, XLSX, CSV — pois so ha um export por vez). */
  exportandoDespesas = false;

  /** Itens do menu popup "Exportar" (PDF / Excel .xlsx / CSV). Montado on-demand na
   *  abertura do menu (`abrirMenuExportarDespesas`) para refletir o idioma atual. */
  exportarDespesasItens: MenuItem[] = [];

  // ===========================================================================
  // Novo contrato (dialog)
  // ===========================================================================

  dialogNovoContrato = false;
  salvandoContrato = false;

  // ---- Etapas do modal (wizard de 3 passos) ----
  //
  // O formulario tem 16 campos com "criar cadastro" marcado e o aviso de pendencias
  // chegava a listar 9 de uma vez, no fim do scroll. Dividido por ASSUNTO:
  //   1 = quem e o cliente        (3 campos, ou 10 com "criar cadastro")
  //   2 = termos do contrato      (5 campos, sempre)
  //   3 = desconto e comissao     (1 campo + a explicacao, sempre)
  // O ganho nao e estetico: cada etapa cobra so as SUAS pendencias, ao lado do botao.
  //
  // Sao SEMPRE 3 etapas — inclusive com o checkbox "criar cadastro" marcado, que so engorda
  // a etapa 1. Numero de passos estavel e o que torna o indicador honesto.

  /** Etapa aberta do modal "Novo contrato". */
  etapaNovoContrato: 1 | 2 | 3 = 1;
  /**
   * "Continuar" foi clicado com uma checagem de email ainda em voo.
   *
   * ⚠️ Sem isto o contador avanca e a resposta chega depois, marcando o "criar cadastro"
   * (ou bloqueando o email) numa etapa que ele nao esta mais vendo — 7 campos obrigatorios
   * apareceriam fora de tela e o botao final ficaria apagado sem explicacao.
   */
  private avancoPendente = false;
  /**
   * Ultimo email que o pipeline efetivamente respondeu (eco da resposta).
   *
   * Serve para saber se ainda ha checagem por vir: `buscandoEmpresas` so vira `true` DEPOIS
   * do `debounceTime(400)`, entao entre a tecla e o disparo existe uma janela em que a
   * checagem esta pendente e nenhuma flag de loading acusa isso. Comparar com o email atual
   * cobre essa janela — e, quando o `distinctUntilChanged` engole um email repetido, este
   * campo ja e igual ao atual, entao nada fica pendente para sempre.
   */
  private ultimoEmailChecado: string | null = null;
  /** Titulo da etapa: recebe o foco a cada troca (leitores de tela e teclado). */
  @ViewChild('tituloEtapaNovoContrato') private tituloEtapaRef?: ElementRef<HTMLElement>;
  /** Erro da ultima tentativa de criar contrato, exibido FIXO no modal (o toast some em 3s). */
  erroNovoContrato: string | null = null;
  carregandoOpcoesContrato = false;

  clientesDisponiveis: ClienteDisponivelContratoDTO[] = [];
  /** Contas encontradas pelo email EXATO digitado (checa se o cliente já tem conta).
   *  Por segurança o backend devolve somente o eco do email — sem nome e sem id. */
  empresasEncontradas: ContaExistenteContratoDTO[] = [];
  buscandoEmpresas = false;
  /** Debounce da busca de conta existente por email. */
  private buscaEmpresaSubject$ = new Subject<string>();

  // ---- Auto-marcacao do "criar cadastro" (o email digitado nao tem conta) ----
  //
  // Regras que valem para TODO este bloco:
  //  1. So auto-marca sobre resposta 200 com lista vazia. Falha na chamada nunca marca.
  //  2. So auto-marca no BLUR do campo (valor estavel). A regex de email casa com prefixos
  //     ("cliente@gmail.c" enquanto se digita ".com"), entao marcar a cada tecla faria os 8
  //     campos do cadastro aparecerem e sumirem, empurrando o resto do modal (que tem scroll).
  //  3. Intencao MANUAL sempre vence: desmarcou a mao, nao re-marcamos aquele email; e a
  //     auto-desmarcacao so desfaz o que NOS marcamos.

  /** A ultima checagem por email FALHOU (rede/permissao) — o campo nao pode ser interpretado. */
  erroBuscaEmail = false;
  /**
   * Email (normalizado) para o qual o backend confirmou, com 200 + lista vazia, que NAO existe
   * conta. Unica base legitima da auto-marcacao. `null` = nao sabemos.
   */
  private emailSemConta: string | null = null;
  /**
   * Email para o qual o "criar cadastro" foi marcado PELO SISTEMA. `null` quando o estado atual
   * do checkbox e escolha do contador — e nesse caso nao mexemos nele.
   */
  private criarContaAutoMarcadoParaEmail: string | null = null;
  /**
   * Emails que o contador desmarcou A MAO. A auto-marcacao nao reincide sobre eles: ele ja
   * disse que prefere o convite.
   */
  private emailsComDesmarqueManual = new Set<string>();
  /**
   * Libera a auto-marcacao. Fica `true` no blur do campo email (valor estavel) e volta a
   * `false` assim que o contador digita de novo.
   */
  private autoMarcacaoLiberada = false;
  /**
   * Contador de aberturas do modal, usado como PREFIXO do valor emitido na busca.
   *
   * ⚠️ Sem ele o `distinctUntilChanged` do pipeline compara apenas o email e engole a
   * consulta quando o contador fecha o modal e digita O MESMO email de novo — o estado tinha
   * sido zerado por `limparEstadoBuscaEmail()`, então a tela ficava sem aviso e sem
   * auto-marcação. Trocando de sessão o valor emitido muda, a consulta passa, e DENTRO da
   * mesma sessão o descarte de email repetido continua valendo.
   */
  private sessaoBuscaEmail = 0;
  /** Opcoes do dropdown de cupom (value = id do cupom). */
  cuponsDisponiveis: { label: string; value: number }[] = [];
  /** Cupons crus carregados (para eventual exibicao de detalhes). */
  cuponsCarregados: CupomContadorConsultaDTO[] = [];

  // ---- Opcoes do bloco "criar cadastro" (mesmas fontes do /signup) ----

  /** Opcoes do dropdown "Atividade da empresa"; value = NOME do enum (ex.: "ADVOGADOS"). */
  atividadesEmpresa: { label: string; value: string }[] = [];
  carregandoAtividades = false;
  erroAtividades = false;
  /** Idioma em que as atividades foram carregadas (os rotulos vem traduzidos do backend). */
  private atividadesIdioma: string | null = null;

  /** Opcoes do dropdown "Cidade"; value = id da cidade. */
  cidades: CidadeOption[] = [];
  carregandoCidades = false;
  erroCidades = false;

  /**
   * Opcoes do dropdown "Moeda padrao" da empresa do cliente; value = NOME do enum.
   * Mesma fonte do /signup (constante local do enum) — sem request extra.
   */
  readonly moedasEmpresa = MOEDA_OPTIONS;

  /** Modelo do formulario de novo contrato. */
  novoContrato = this.contratoVazio();

  // Popup informativo quando o cliente ainda NAO tem conta (backend retornou CONVITE).
  dialogConviteVisivel = false;
  conviteMensagem = '';

  constructor(
    private contadorService: ContadorService,
    private despesaService: DespesaAcessoriaService,
    private messageService: MessageService,
    private translate: TranslateService,
    private atividadeEmpresaService: AtividadeEmpresaService,
    private cidadeService: CidadeService,
    private router: Router,
  ) {}

  // ===========================================================================
  // Ciclo de vida
  // ===========================================================================

  ngOnInit(): void {
    this.carregarSituacao();

    // Checagem de conta existente por email com debounce — avisa o contador antes de
    // ele optar por criar um cadastro novo. Só consulta quando o email está completo:
    // nada trafega a partir de fragmentos (evita varredura da base).
    this.buscaEmpresaSubject$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => (this.buscandoEmpresas = true)),
        switchMap((termoComSessao) => {
          // O valor chega como "<sessao>|<email>"; a sessão só existe para o
          // `distinctUntilChanged` acima distinguir aberturas diferentes do modal.
          const email = termoComSessao.slice(termoComSessao.indexOf('|') + 1).trim().toLowerCase();
          // Fragmento: nem consulta o backend. `consultou: false` impede a auto-marcacao,
          // porque "vazio por nao ter perguntado" nao prova que a conta nao existe.
          if (!isEmailValido(email)) {
            return of<ResultadoBuscaEmail>({ email, contas: [], erro: false, consultou: false });
          }
          return this.contadorService.buscarEmpresasParaContrato(email).pipe(
            map((contas) => ({ email, contas: contas || [], erro: false, consultou: true })),
            // Falha NAO vira "email sem conta": marca `erro` e a auto-marcacao se abstem.
            catchError(() => of<ResultadoBuscaEmail>({ email, contas: [], erro: true, consultou: true })),
          );
        }),
      )
      .subscribe((res) => {
        this.empresasEncontradas = res.contas;
        this.erroBuscaEmail = res.erro;
        this.buscandoEmpresas = false;
        this.ultimoEmailChecado = res.email;
        // Ausencia de conta so e afirmada com 200 comprovado + lista vazia.
        this.emailSemConta = res.consultou && !res.erro && res.contas.length === 0 ? res.email : null;
        // A conta pode ter passado a existir (typo corrigido): desfaz o que NOS marcamos.
        this.revisarAutoMarcacaoCriarConta();
        // O blur pode ter acontecido antes desta resposta chegar; agora da para decidir.
        this.aplicarAutoMarcacaoCriarConta();
        // "Continuar" clicado antes desta resposta: decide agora se avanca ou fica.
        this.resolverAvancoPendente();
      });
  }

  // ===========================================================================
  // Situacao (decide o branch)
  // ===========================================================================

  carregarSituacao(): void {
    this.carregandoSituacao = true;
    this.erroSituacao = false;

    this.contadorService.minhaSituacao().subscribe({
      next: (res) => {
        this.situacao = res;
        this.carregandoSituacao = false;
        if (res.contador === true) {
          this.carregarDashboard();
        }
      },
      error: () => {
        this.situacao = null;
        this.erroSituacao = true;
        this.carregandoSituacao = false;
      },
    });
  }

  /** Carrega resumo e ranking. A tabela e disparada pelo (onLazyLoad) do p-table. */
  private carregarDashboard(): void {
    // "Meus contratos" e a aba 0 (ja aberta no load), entao as opcoes do filtro
    // de Estado sao construidas aqui — nao no onTabChange (que so dispara p/ 1 e 2).
    this.buildContratosStatusOptions();
    this.carregarResumo();
    this.carregarRanking();
  }

  /** Callback do painel de solicitar (branch nao-contador): re-decide o branch. */
  onSolicitado(): void {
    this.carregarSituacao();
  }

  // ===========================================================================
  // Resumo
  // ===========================================================================

  carregarResumo(): void {
    this.carregandoResumo = true;
    this.erroResumo = false;

    this.contadorService.resumo().subscribe({
      next: (res) => {
        this.resumo = res;
        this.carregandoResumo = false;
      },
      error: (err) => {
        this.resumo = null;
        this.erroResumo = true;
        this.carregandoResumo = false;
        this.notificarErro(err);
      },
    });
  }

  // ===========================================================================
  // Ranking
  // ===========================================================================

  carregarRanking(): void {
    this.carregandoRanking = true;
    this.erroRanking = false;

    this.contadorService.ranking().subscribe({
      next: (res) => {
        this.ranking = res;
        this.carregandoRanking = false;
      },
      error: (err) => {
        this.ranking = null;
        this.erroRanking = true;
        this.carregandoRanking = false;
        this.notificarErro(err);
      },
    });
  }

  // ===========================================================================
  // Contratos (tabela lazy)
  // ===========================================================================

  carregarContratos(page = 0, size = this.rows): void {
    this.carregandoContratos = true;
    this.erroContratos = false;

    this.contadorService
      .listarContratos(page, size, this.montarSortContratos(), this.montarFiltrosContratos())
      .subscribe({
        next: (res) => {
          this.contratos = res.content || [];
          this.totalRecords = res.totalElements ?? 0;
          this.first = page * size;
          this.carregandoContratos = false;
        },
        error: (err) => {
          this.contratos = [];
          this.totalRecords = 0;
          this.erroContratos = true;
          this.carregandoContratos = false;
          this.notificarErro(err);
        },
      });
  }

  /** Situacoes possiveis do contrato — ordem exibida no filtro de Estado. */
  private static readonly SITUACOES_CONTRATO: StatusContratoContador[] = [
    'PENDENTE',
    'ATIVO',
    'CANCELADO',
    'RECUSADO',
    'VENCIDO',
  ];

  /** Opcoes do filtro de Estado; primeira opcao ("Todos os estados") = null.
   *  Reusa `labelStatus` (mesmos rotulos da coluna Estado da tabela). */
  private buildContratosStatusOptions(): void {
    this.contratosStatusOptions = [
      {
        value: null,
        label: this.translate.instant('contador.dashboard.contratos.filtro_estado_todos'),
      },
      ...AreaContadorComponent.SITUACOES_CONTRATO.map((s) => ({
        value: s,
        label: this.labelStatus(s),
      })),
    ];
  }

  /** Monta os filtros (Estado + periodo de vencimento) para o GET de contratos e
   *  para o relatorio. `null -> undefined` (buildQueryParams descarta undefined). */
  private montarFiltrosContratos(): ContratoContadorRelatorioFiltroDTO {
    return {
      status: this.contratosStatus ?? undefined,
      de: this.contratosDe,
      ate: this.contratosAte,
    };
  }

  /** Aplica o filtro de Estado (dropdown) e volta para a primeira pagina. */
  onContratosFiltroChange(): void {
    this.first = 0;
    this.carregarContratos(0, this.rows);
  }

  /**
   * Aplica o filtro de periodo de VENCIMENTO (app-periodo-filtro). Converte as
   * datas `dd/MM/yyyyTHH:mm` para `yyyy-MM-dd` (de/ate) e recarrega da 1a pagina.
   * `showAll` (datas vazias) => sem intervalo (todos os contratos).
   */
  onContratosPeriodoChanged(periodo: PeriodoFiltro): void {
    this.contratosDe = this.dataFiltroParaIso(periodo?.dataInicio);
    this.contratosAte = this.dataFiltroParaIso(periodo?.dataFim);
    this.first = 0;
    this.carregarContratos(0, this.rows);
  }

  // ---------------------------------------------------------------------------
  // Acao — exportar (menu PDF / Excel / CSV dos contratos; filtros atuais da aba)
  // ---------------------------------------------------------------------------

  /**
   * Monta os itens do menu "Exportar": PDF reusa o fluxo Base64 existente
   * (`exportarContratosPdf`); Excel/CSV usam o export binario
   * (`exportarContratosArquivo`). Espelha a aba "Gastos acessorios".
   */
  private montarExportarContratosItens(): void {
    this.exportarContratosItens = [
      {
        label: this.translate.instant('contador.dashboard.contratos.exportar.pdf'),
        icon: 'pi pi-file-pdf',
        command: () => this.exportarContratosPdf(),
      },
      {
        label: this.translate.instant('contador.dashboard.contratos.exportar.excel'),
        icon: 'pi pi-file-excel',
        command: () => this.exportarContratosArquivo('xlsx'),
      },
      {
        label: this.translate.instant('contador.dashboard.contratos.exportar.csv'),
        icon: 'pi pi-file',
        command: () => this.exportarContratosArquivo('csv'),
      },
    ];
  }

  /**
   * Reconstroi os itens e abre o menu "Exportar". Montar na abertura garante
   * rotulos no idioma atual sem subscription de `onLangChange`/OnDestroy.
   */
  abrirMenuExportarContratos(event: Event, menu: Menu): void {
    this.montarExportarContratosItens();
    menu.toggle(event);
  }

  /**
   * Exporta o PDF dos contratos respeitando os filtros ATUAIS da aba (Estado +
   * periodo de vencimento) — SEM page/size (imprime TODAS as linhas ate o teto
   * do backend; sort default do backend). Sucesso => baixa o PDF; Base64 vazio
   * => toast "sem dados" (defensivo: o backend gera PDF valido mesmo com 0
   * linhas); erro => `tratarErroExportarContratosPdf`. A flag `exportandoContratos`
   * desabilita o botao e barra clique duplo.
   */
  exportarContratosPdf(): void {
    if (this.exportandoContratos) {
      return; // guarda contra clique duplo
    }
    this.exportandoContratos = true;
    this.contadorService
      .gerarRelatorioContratosPdf(this.construirFiltrosRelatorioContratos())
      .subscribe({
        next: (base64) => {
          this.exportandoContratos = false;
          if (base64 && base64.length > 0) {
            FileUtils.abrirPdf(base64, this.nomeArquivoRelatorioContratos());
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('contador.dashboard.contratos.exportar.sem_dados'),
            });
          }
        },
        error: (err) => {
          this.exportandoContratos = false;
          this.tratarErroExportarContratosPdf(err);
        },
      });
  }

  /**
   * Exporta os contratos como .csv/.xlsx com os MESMOS filtros do PDF
   * (`construirFiltrosRelatorioContratos`), sem page/size/sort. 200 com 0 bytes
   * = sem dados (toast, nao baixa). A flag `exportandoContratos` (compartilhada
   * com o PDF) barra clique duplo.
   */
  exportarContratosArquivo(formato: 'csv' | 'xlsx'): void {
    if (this.exportandoContratos) {
      return; // guarda contra clique duplo
    }
    this.exportandoContratos = true;
    this.contadorService
      .gerarRelatorioContratosArquivo(formato, this.construirFiltrosRelatorioContratos())
      .subscribe({
        next: (blob) => {
          this.exportandoContratos = false;
          if (blob && blob.size > 0) {
            FileUtils.baixarBlob(blob, this.nomeArquivoRelatorioContratos(formato));
          } else {
            // Backend responde 200 com 0 bytes quando nao ha contratos no filtro.
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('contador.dashboard.contratos.exportar.sem_dados'),
            });
          }
        },
        error: (err) => {
          this.exportandoContratos = false;
          this.tratarErroExportarContratosArquivo(err);
        },
      });
  }

  /** Filtros do relatorio = MESMOS filtros da tabela (Estado + periodo de
   *  vencimento), SEM page/size (o relatorio imprime tudo; sort default do backend). */
  private construirFiltrosRelatorioContratos(): ContratoContadorRelatorioFiltroDTO {
    return this.montarFiltrosContratos();
  }

  /** Nome do arquivo baixado: `Contratos_AAAAMMDD.{ext}`. O FE define o nome
   *  (ignora o Content-Disposition do backend); o default `pdf` preserva o
   *  callsite antigo do PDF. */
  private nomeArquivoRelatorioContratos(ext: 'pdf' | 'csv' | 'xlsx' = 'pdf'): string {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    return `Contratos_${ano}${mes}${dia}.${ext}`;
  }

  /**
   * Trata os erros do relatorio de contratos (mesma filosofia de
   * `tratarErroExportarDespesasPdf`). O `readText` entrega o corpo do erro como
   * STRING CRUA (JSON nao parseado), entao usamos `extrairMensagemBackend`:
   *  - 403 => chave fixa `sem_permissao` (nao vaza texto tecnico);
   *  - 422 => mensagem amigavel do backend (teto de 5000) com fallback a chave
   *           `limite_excedido`;
   *  - 500/generico => chave fixa `erro`.
   * 401 e tratado globalmente pelo AuthInterceptor (nao tratar aqui).
   */
  private tratarErroExportarContratosPdf(err: any): void {
    const status = err?.status;

    if (status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('contador.dashboard.contratos.exportar.sem_permissao'),
      });
      return;
    }

    if (status === 422) {
      this.messageService.add({
        severity: 'error',
        summary:
          this.extrairMensagemBackend(err) ||
          this.translate.instant('contador.dashboard.contratos.exportar.limite_excedido'),
      });
      return;
    }

    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('contador.dashboard.contratos.exportar.erro'),
    });
  }

  /**
   * Roteia a mensagem i18n do export binario por `err.status`: `readBlob` entrega
   * o corpo do erro como Blob, nao parseavel de forma sincrona (nunca ler
   * `.message`). 422 cobre teto de 5000 e formato invalido; 401 e do
   * AuthInterceptor global.
   */
  private tratarErroExportarContratosArquivo(err: any): void {
    const status = err?.status;
    let chave = 'contador.dashboard.contratos.exportar.erro'; // generico/500
    if (status === 403) {
      chave = 'contador.dashboard.contratos.exportar.sem_permissao';
    } else if (status === 422) {
      chave = 'contador.dashboard.contratos.exportar.limite_excedido';
    }
    this.messageService.add({ severity: 'error', summary: this.translate.instant(chave) });
  }

  /**
   * Sort da tabela "Mis contratos" (server-side). Só campos reais da entidade
   * ordenam: Estado (`status`) e Valor mensual (`valorMensal`). Cliente (nome) e os
   * agregados (Gastos aprobados/A cobrar) são calculados em memória e não ordenam.
   */
  private contratosSortField: string | null = null;
  private contratosSortOrder = 0; // 1 asc, -1 desc, 0 = default do backend

  private montarSortContratos(): string {
    if (!this.contratosSortField || this.contratosSortOrder === 0) {
      return 'dataAssociacao,desc';
    }
    return `${this.contratosSortField},${this.contratosSortOrder === 1 ? 'asc' : 'desc'}`;
  }

  /** Lazy-load do p-table (paginacao + ordenacao server-side) — padrao IndicacoesComponent. */
  onLazyLoadContratos(event: TableLazyLoadEvent): void {
    const size = event.rows ?? this.rows;
    const page = Math.floor((event.first ?? 0) / size);
    this.rows = size;
    // A propria p-table lazy dispara o onLazyLoad ao clicar num cabecalho ordenavel,
    // ja com sortField/sortOrder (sem um (onSort) separado, que recarregaria em dobro).
    this.contratosSortField = typeof event.sortField === 'string' ? event.sortField : null;
    this.contratosSortOrder = this.contratosSortField ? (event.sortOrder ?? 0) : 0;
    this.carregarContratos(page, size);
  }

  /** Recarrega a pagina atual de contratos (acao "tentar novamente"). */
  /** Abre o Operar Cliente ja com o cliente do contrato selecionado. */
  abrirOperarCliente(contrato: ContratoContadorConsultaDTO): void {
    if (contrato?.configuracaoEmpresaId == null) return;
    this.router.navigate(['/contador/operar'], {
      queryParams: { clienteId: contrato.configuracaoEmpresaId },
    });
  }

  recarregarContratos(): void {
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
    this.carregarContratos(page, this.rows);
  }

  /** Troca de pagina pelo paginador externo. Como a tabela e lazy,
   *  recarregamos a pagina solicitada do backend. */
  onPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rows;
    const page = event.page ?? 0;
    this.rows = size;
    this._lastRows = size;
    this.carregarContratos(page, size);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega.
   *  O retorno antecipado ignora o disparo do dropdown sem mudanca de valor. */
  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) {
      return;
    }
    this._lastRows = this.rows;
    this.first = 0;
    this.carregarContratos(0, this.rows);
  }

  // ===========================================================================
  // Aba "Perto do vencimento"
  // ===========================================================================

  /** Troca de aba do p-tabView: carrega a lista de proximos na 1a vez que a aba abre. */
  onTabChange(event: { index: number }): void {
    if (event.index === 1 && !this.proximosCarregouUmaVez) {
      this.proximosCarregouUmaVez = true;
      this.carregarProximos(0);
    }
    if (event.index === 2 && !this.despesasCarregouUmaVez) {
      this.despesasCarregouUmaVez = true;
      this.buildDespesasStatusOptions();
      this.carregarIndicadosDoFiltro();
      this.carregarDespesas(0);
    }
  }

  // ===========================================================================
  // Aba "Gastos acessorios" (consolidado)
  // ===========================================================================

  /** Situacoes possiveis do gasto — ordem exibida no filtro. */
  private static readonly SITUACOES_DESPESA: StatusDespesaAcessoria[] = [
    'PENDENTE',
    'ATIVA',
    'RECUSADA',
    'CANCELADA',
  ];

  /** Opcoes do filtro de situacao; primeira opcao ("Todas") = null. */
  private buildDespesasStatusOptions(): void {
    this.despesasStatusOptions = [
      {
        value: null,
        label: this.translate.instant('contador_operar.despesa_acessoria.filtros.situacao_todas'),
      },
      ...AreaContadorComponent.SITUACOES_DESPESA.map((s) => ({
        value: s,
        label: this.statusDespesaLabel(s),
      })),
    ];
  }

  /**
   * Opcoes do filtro por INDICADO: os clientes do contador (mesma fonte do seletor
   * de Operar Cliente). Falha aqui nao quebra a aba — o filtro fica so com "Todos".
   */
  private carregarIndicadosDoFiltro(): void {
    const todos = {
      value: null,
      label: this.translate.instant('contador.dashboard.despesas.filtro_indicado_todos'),
    };
    this.contadorService.listarClientes(0, 200).subscribe({
      next: (res) => {
        this.despesasIndicadoOptions = [
          todos,
          ...(res.content || []).map((c) => ({ value: c.empresaIndicadaId, label: c.nome })),
        ];
      },
      error: () => {
        this.despesasIndicadoOptions = [todos];
      },
    });
  }

  /** Rotulo localizado da situacao do gasto (tag e filtro). */
  statusDespesaLabel(status: string): string {
    const chave = `contador_operar.despesa_acessoria.status.${status.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? status : traduzido;
  }

  /** Cor (severity do p-tag) conforme a situacao do gasto. */
  statusDespesaSeverity(status: string): 'success' | 'warning' | 'danger' | 'info' {
    switch (status) {
      case 'PENDENTE':
        return 'warning';
      case 'ATIVA':
        return 'success';
      case 'RECUSADA':
        return 'danger';
      default: // CANCELADA
        return 'info';
    }
  }

  /**
   * Nome do tipo de gasto: o backend serializa o enum como objeto localizado
   * ({ enum, nome }); mantem suporte a string crua por robustez/legado.
   */
  tipoDespesaNome(tipo: { enum?: string; nome?: string } | string | null | undefined): string {
    if (tipo == null) {
      return '-';
    }
    if (typeof tipo === 'object') {
      return tipo.nome || tipo.enum || '-';
    }
    const chave = `enums.tipo_despesa.${tipo}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? tipo : traduzido;
  }

  /** Carrega a pagina informada da listagem consolidada de gastos. */
  carregarDespesas(page: number): void {
    this.carregandoDespesas = true;
    this.erroDespesas = false;

    this.despesaService
      .listarDoContador(page, this.despesasRows, this.montarSortDespesas(), this.montarFiltrosDespesas())
      .subscribe({
        next: (res) => {
          this.despesas = res.content || [];
          this.despesasTotalRecords = res.totalElements ?? 0;
          this.despesasFirst = page * this.despesasRows;
          this.carregandoDespesas = false;
        },
        error: () => {
          this.despesas = [];
          this.despesasTotalRecords = 0;
          this.erroDespesas = true;
          this.carregandoDespesas = false;
        },
      });
  }

  /** Monta os filtros (indicado, situacao e periodo) para o GET consolidado. */
  private montarFiltrosDespesas(): DespesaAcessoriaContadorFiltroDTO {
    return {
      clienteEmpresaId: this.despesasClienteEmpresaId ?? undefined,
      status: this.despesasStatus ?? undefined,
      de: this.despesasDe,
      ate: this.despesasAte,
    };
  }

  // ===========================================================================
  // Acao — exportar (menu PDF / Excel / CSV dos gastos acessorios, filtros atuais)
  // ===========================================================================

  /**
   * Monta os 3 itens do menu popup "Exportar" (PDF / Excel .xlsx / CSV) com os
   * rotulos traduzidos no idioma atual. Espelha o `RepasseComissoesComponent`: PDF
   * reusa o fluxo Base64 existente (`exportarDespesasPdf`); Excel/CSV usam o export
   * binario (`exportarDespesasArquivo`).
   */
  private montarExportarDespesasItens(): void {
    this.exportarDespesasItens = [
      {
        label: this.translate.instant('contador.dashboard.despesas.exportar.pdf'),
        icon: 'pi pi-file-pdf',
        command: () => this.exportarDespesasPdf(),
      },
      {
        label: this.translate.instant('contador.dashboard.despesas.exportar.excel'),
        icon: 'pi pi-file-excel',
        command: () => this.exportarDespesasArquivo('xlsx'),
      },
      {
        label: this.translate.instant('contador.dashboard.despesas.exportar.csv'),
        icon: 'pi pi-file',
        command: () => this.exportarDespesasArquivo('csv'),
      },
    ];
  }

  /**
   * Abre o menu popup "Exportar" reconstruindo os itens (rotulos no idioma atual)
   * imediatamente antes do toggle. Evita rotulos "presos" no idioma inicial sem
   * exigir OnDestroy/subscription do onLangChange (Opcao B — menu on-demand).
   */
  abrirMenuExportarDespesas(event: Event, menu: Menu): void {
    this.montarExportarDespesasItens();
    menu.toggle(event);
  }

  /**
   * Exporta o PDF dos gastos acessorios respeitando os filtros ATUAIS da aba
   * (indicado, situacao, periodo, ordenacao) — SEM page/size (imprime TODAS as
   * linhas ate o teto do backend). Sucesso => baixa o PDF; Base64 vazio => toast
   * "sem dados" (defensivo: o backend gera PDF valido mesmo com 0 linhas); erro =>
   * `tratarErroExportarDespesasPdf`. A flag `exportandoDespesas` desabilita o botao
   * e barra clique duplo.
   */
  exportarDespesasPdf(): void {
    if (this.exportandoDespesas) {
      return; // guarda contra clique duplo
    }
    this.exportandoDespesas = true;
    this.despesaService
      .gerarRelatorioDespesasPdf(this.construirFiltrosRelatorioDespesas())
      .subscribe({
        next: (base64) => {
          this.exportandoDespesas = false;
          if (base64 && base64.length > 0) {
            FileUtils.abrirPdf(base64, this.nomeArquivoRelatorioDespesas());
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant(
                'contador.dashboard.despesas.exportar_pdf.sem_dados',
              ),
            });
          }
        },
        error: (err) => {
          this.exportandoDespesas = false;
          this.tratarErroExportarDespesasPdf(err);
        },
      });
  }

  /**
   * Exporta os gastos acessorios como arquivo binario (.csv/.xlsx) com os filtros
   * ATUAIS da aba (mesmos do PDF, via `construirFiltrosRelatorioDespesas` — SEM
   * page/size). Corpo => baixa via `FileUtils.baixarBlob`; 0 bytes => toast "sem
   * dados" (reusa a chave do PDF) sem baixar; erro => `tratarErroExportarDespesasArquivo`.
   * A flag `exportandoDespesas` (compartilhada com o PDF) evita clique duplo.
   */
  exportarDespesasArquivo(formato: 'csv' | 'xlsx'): void {
    if (this.exportandoDespesas) {
      return; // guarda contra clique duplo
    }
    this.exportandoDespesas = true;
    this.despesaService
      .gerarRelatorioDespesasArquivo(formato, this.construirFiltrosRelatorioDespesas())
      .subscribe({
        next: (blob) => {
          this.exportandoDespesas = false;
          if (blob && blob.size > 0) {
            FileUtils.baixarBlob(blob, this.nomeArquivoRelatorioDespesas(formato));
          } else {
            // Backend responde 200 com 0 bytes quando nao ha gastos no filtro.
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant(
                'contador.dashboard.despesas.exportar_pdf.sem_dados',
              ),
            });
          }
        },
        error: (err) => {
          this.exportandoDespesas = false;
          this.tratarErroExportarDespesasArquivo(err);
        },
      });
  }

  /**
   * Trata o erro do export binario. Como `readBlob` entrega `err.error` como Blob
   * (nao parseavel de forma sincrona), roteamos a mensagem i18n por `err.status`
   * (nao tentamos ler `message` do Blob):
   *  - 403 => `exportar_pdf.sem_permissao` (reuso — nao vaza texto tecnico);
   *  - 422 => `exportar.limite_excedido` (teto de 5000 OU formato invalido);
   *  - 500/generico => `exportar_pdf.erro` (reuso).
   * 401 e tratado globalmente pelo AuthInterceptor (nao tratar aqui).
   */
  private tratarErroExportarDespesasArquivo(err: any): void {
    const status = err?.status;
    let chave = 'contador.dashboard.despesas.exportar_pdf.erro'; // generico/500 (reuso)
    if (status === 403) {
      chave = 'contador.dashboard.despesas.exportar_pdf.sem_permissao'; // reuso
    } else if (status === 422) {
      chave = 'contador.dashboard.despesas.exportar.limite_excedido';
    }
    this.messageService.add({ severity: 'error', summary: this.translate.instant(chave) });
  }

  /**
   * Monta os filtros do relatorio a partir do estado atual da aba. Reaproveita
   * `montarFiltrosDespesas()` (indicado/situacao/periodo) e ADICIONA `sort`. NAO
   * inclui page/size (o relatorio imprime tudo). `null -> undefined` ja e feito por
   * `montarFiltrosDespesas`; `buildQueryParams` descarta as chaves `undefined`.
   */
  private construirFiltrosRelatorioDespesas(): DespesaAcessoriaRelatorioPdfFiltroDTO {
    return {
      ...this.montarFiltrosDespesas(),
      sort: this.montarSortDespesas(),
    };
  }

  /** Nome do arquivo baixado por extensao: `Gastos_Accesorios_AAAAMMDD.{ext}`
   *  (.pdf/.csv/.xlsx; a aba e ES; sugestao do contrato). O FE define o nome (o
   *  backend usa nome fixo no Content-Disposition, ignorado aqui). */
  private nomeArquivoRelatorioDespesas(ext: 'pdf' | 'csv' | 'xlsx' = 'pdf'): string {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    return `Gastos_Accesorios_${ano}${mes}${dia}.${ext}`;
  }

  /**
   * Trata os erros do relatorio (mesma filosofia de `tratarErroExportarPdf` de
   * comissoes). Como o relatorio usa `readText` (responseType 'text'), o corpo do
   * erro chega como STRING CRUA (o JSON NAO e parseado pelo HttpClient), entao
   * usamos `extrairMensagemBackend` para nao cair sempre no fallback.
   *  - 403 => chave fixa `sem_permissao` (nao vaza texto tecnico);
   *  - 422 => mensagem amigavel do backend (teto de 5000) com fallback a chave
   *           `limite_excedido`;
   *  - 500/generico => chave fixa `erro`.
   * 401 e tratado globalmente pelo AuthInterceptor (nao tratar aqui).
   */
  private tratarErroExportarDespesasPdf(err: any): void {
    const status = err?.status;

    if (status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant(
          'contador.dashboard.despesas.exportar_pdf.sem_permissao',
        ),
      });
      return;
    }

    if (status === 422) {
      this.messageService.add({
        severity: 'error',
        summary:
          this.extrairMensagemBackend(err) ||
          this.translate.instant('contador.dashboard.despesas.exportar_pdf.limite_excedido'),
      });
      return;
    }

    // 500 e demais: chave generica amigavel (nao expoe corpo tecnico ao leigo).
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('contador.dashboard.despesas.exportar_pdf.erro'),
    });
  }

  /**
   * Extrai a mensagem amigavel do backend de forma robusta ao responseType.
   * `read` (JSON) entrega `err.error` como OBJETO ({ message }); `readText` (este
   * relatorio) entrega `err.error` como STRING CRUA (JSON nao parseado). Cobre os
   * dois: objeto -> `.message`; string -> `JSON.parse` -> `.message`. Corpo ausente
   * ou texto nao-JSON retorna `undefined` (o chamador aplica o fallback i18n).
   * Replicado de `RepasseComissoesComponent.extrairMensagemBackend`.
   */
  private extrairMensagemBackend(err: any): string | undefined {
    const corpo = err?.error;
    if (!corpo) {
      return undefined;
    }
    if (typeof corpo === 'object') {
      return typeof corpo.message === 'string' ? corpo.message : undefined;
    }
    if (typeof corpo === 'string') {
      try {
        const parsed = JSON.parse(corpo);
        return typeof parsed?.message === 'string' ? parsed.message : undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  /** Aplica o filtro de periodo (dataPagamento) e volta para a primeira pagina. */
  onDespesasPeriodoChanged(periodo: PeriodoFiltro): void {
    this.despesasDe = this.dataFiltroParaIso(periodo?.dataInicio);
    this.despesasAte = this.dataFiltroParaIso(periodo?.dataFim);
    this.despesasFirst = 0;
    this.carregarDespesas(0);
  }

  /** Aplica os filtros de indicado/situacao (dropdowns) e recarrega. */
  onDespesasFiltroChange(): void {
    this.despesasFirst = 0;
    this.carregarDespesas(0);
  }

  /**
   * Sort da tabela "Gastos accesorios" (server-side; a tabela usa [customSort]).
   * Campos reais da entidade: Importe (`valor`), Fecha de pago (`dataPagamento`),
   * Mes de referencia (`mesReferencia`), Tipo (`tipoDespesa`) e Situación (`status`).
   * Referido (nome do cliente) é resolvido em memória e não ordena.
   */
  despesasSortField: string | null = null;
  despesasSortOrder = 0;

  private montarSortDespesas(): string {
    if (!this.despesasSortField || this.despesasSortOrder === 0) {
      return 'dataPagamento,desc';
    }
    return `${this.despesasSortField},${this.despesasSortOrder === 1 ? 'asc' : 'desc'}`;
  }

  /**
   * Ordenacao por cabecalho (server-side). A p-table usa [customSort] (nao reordena
   * em memoria — os dados vem paginados); volta para a primeira pagina e recarrega.
   * Guard de "mudanca real" evita a recarga em dobro que o PrimeNG dispara ao trocar
   * [value] numa tabela NAO-lazy com [customSort].
   */
  onDespesasSort(event: { field?: string | string[]; order?: number }): void {
    const campo = typeof event.field === 'string' ? event.field : null;
    const ordem = campo ? (event.order ?? 0) : 0;
    if (campo === this.despesasSortField && ordem === this.despesasSortOrder) {
      return;
    }
    this.despesasSortField = campo;
    this.despesasSortOrder = ordem;
    this.despesasFirst = 0;
    this.carregarDespesas(0);
  }

  /** Troca de pagina pelo paginador do rodape. */
  /**
   * Abre os titulos do cliente em contas a receber (mesma ficha da lista de
   * Clientes). O id da pessoa vem da listagem; gasto cujo cliente ainda nao
   * virou pessoa — porque nao gerou titulo — fica sem clique.
   */
  abrirTitulosDoCliente(despesa: DespesaAcessoriaContadorDTO): void {
    const pessoaId = despesa?.pessoaId;
    if (!pessoaId) return;
    this.router.navigate(['/contas-a-receber/pessoa', pessoaId], {
      queryParams: {
        nome: despesa.nomeCliente || '',
        tipo: 'ENTRADA',
        // Leva o "voltar" da ficha de titulos de volta para ca.
        origem: 'contador-area',
      },
    });
  }

  temTitulosParaAbrir(despesa: DespesaAcessoriaContadorDTO): boolean {
    return !!despesa?.pessoaId;
  }

  onDespesasPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.despesasRows;
    const page = event.page ?? 0;
    this.despesasRows = size;
    this._despesasLastRows = size;
    this.carregarDespesas(page);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega. */
  onDespesasRowsPerPageChange(): void {
    if (this.despesasRows === this._despesasLastRows) {
      return;
    }
    this._despesasLastRows = this.despesasRows;
    this.despesasFirst = 0;
    this.carregarDespesas(0);
  }

  /** Rótulo localizado do mês de referência (para a coluna da tabela). */
  mesReferenciaLabel(mes: string | null | undefined): string {
    if (!mes) {
      return '-';
    }
    const chave = `contador_operar.despesa_acessoria.mes_referencia_valores.${mes.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? mes : traduzido;
  }

  carregarProximos(page: number): void {
    this.carregandoProximos = true;
    this.erroProximos = false;
    // Selecao vale para a visao atual: recarga (filtro/pagina) limpa a selecao.
    this.proximosSelecionados = [];

    this.contadorService
      .listarProximosVencimento(
        this.proximosDe,
        this.proximosAte,
        page,
        this.proximosRows,
        this.montarSortProximos(),
      )
      .subscribe({
        next: (res) => {
          this.proximos = res.content || [];
          this.proximosTotalRecords = res.totalElements ?? 0;
          this.proximosFirst = page * this.proximosRows;
          this.carregandoProximos = false;
        },
        error: (err) => {
          this.proximos = [];
          this.proximosTotalRecords = 0;
          this.erroProximos = true;
          this.carregandoProximos = false;
          this.notificarErro(err);
        },
      });
  }

  /**
   * Sort da tabela "Cerca del vencimiento" (server-side). Campos reais da entidade:
   * Valor mensual (`valorMensal`), Vencimiento (`dataVencimento`), Mes de referencia
   * (`mesReferencia`) e Día de cobro (`diaCobranca`). Cliente (nome) é resolvido em
   * memória e não ordena.
   */
  private proximosSortField: string | null = null;
  private proximosSortOrder = 0;

  private montarSortProximos(): string {
    if (!this.proximosSortField || this.proximosSortOrder === 0) {
      return 'dataVencimento,asc';
    }
    return `${this.proximosSortField},${this.proximosSortOrder === 1 ? 'asc' : 'desc'}`;
  }

  /** Lazy-load da tabela de proximos (paginacao + ordenacao server-side). */
  onProximosLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows ?? this.proximosRows;
    const page = Math.floor((event.first ?? 0) / size);
    this.proximosRows = size;
    this._proximosLastRows = size;
    this.proximosSortField = typeof event.sortField === 'string' ? event.sortField : null;
    this.proximosSortOrder = this.proximosSortField ? (event.sortOrder ?? 0) : 0;
    this.carregarProximos(page);
  }

  onProximosPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.proximosRows;
    const page = event.page ?? 0;
    this.proximosRows = size;
    this._proximosLastRows = size;
    this.carregarProximos(page);
  }

  onProximosRowsPerPageChange(): void {
    if (this.proximosRows === this._proximosLastRows) {
      return;
    }
    this._proximosLastRows = this.proximosRows;
    this.proximosFirst = 0;
    this.carregarProximos(0);
  }

  /**
   * Aplica o filtro de periodo de vencimento (app-periodo-filtro). Converte as
   * datas `dd/MM/yyyyTHH:mm` para `yyyy-MM-dd` (de/ate) e recarrega da 1a pagina.
   * `showAll` (datas vazias) => sem intervalo (todos os ATIVOS).
   */
  onProximosPeriodoChanged(periodo: PeriodoFiltro): void {
    this.proximosDe = this.dataFiltroParaIso(periodo?.dataInicio);
    this.proximosAte = this.dataFiltroParaIso(periodo?.dataFim);
    this.proximosFirst = 0;
    this.carregarProximos(0);
  }

  /** Converte `dd/MM/yyyyTHH:mm` do app-periodo-filtro para `yyyy-MM-dd`. Vazio => undefined. */
  private dataFiltroParaIso(valor: string | undefined | null): string | undefined {
    if (!valor) {
      return undefined;
    }
    const parteData = valor.split('T')[0];
    const partes = parteData.split('/');
    if (partes.length !== 3) {
      return undefined;
    }
    const [dia, mes, ano] = partes;
    return `${ano}-${mes}-${dia}`;
  }

  // ---------- Gerar parcelas em lote (dialog) ----------

  /** Abre o dialog de geração em lote (só com seleção). Reseta os campos (1 e 1). */
  abrirGerarLote(): void {
    if (this.proximosSelecionados.length === 0) {
      return;
    }
    this.gerarLoteQuantidade = 1;
    this.gerarLotePrimeiroVencimento = null;
    this.gerarLoteQuantidadeErro = '';
    this.gerarLoteConfirmVolumeVisivel = false;
    this.gerarLoteDialogVisivel = true;
  }

  fecharGerarLote(): void {
    this.gerarLoteDialogVisivel = false;
  }

  /** Converte um Date para 'yyyy-MM-dd' (LocalDate do backend); null -> undefined. */
  private formatarDataParaBackend(data: Date | null): string | undefined {
    if (!data) return undefined;
    const y = data.getFullYear();
    const m = String(data.getMonth() + 1).padStart(2, '0');
    const d = String(data.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /**
   * Validacao client-side: quantidade inteira a partir de 1, sem teto maximo (a
   * data do 1º vencimento e opcional). Fonte unica de verdade: alimenta o
   * [disabled] do botao, a mensagem inline e a guarda do envio.
   */
  get gerarLoteValido(): boolean {
    const q = this.gerarLoteQuantidade;
    return q != null && q >= 1;
  }

  /** Total de parcelas que a geracao vai criar (quantidade x contratos). */
  get gerarLoteTotalParcelas(): number {
    return (this.gerarLoteQuantidade ?? 0) * this.proximosSelecionados.length;
  }

  /**
   * Atualiza a mensagem inline enquanto o usuario edita a quantidade. O botao
   * "Gerar" continua desabilitado quando invalido — a mensagem existe para
   * explicar POR QUE ele esta desabilitado (antes o feedback era inalcancavel,
   * porque so era ligado no clique de um botao que nunca era clicavel).
   * Mesmo padrao do dialogo "Confirmar pagamento em LOTE" de repasse-comissoes.
   */
  onGerarLoteQuantidadeChange(): void {
    this.gerarLoteQuantidadeErro = this.gerarLoteValido
      ? ''
      : this.translate.instant('contador.dashboard.proximos.gerar_lote_quantidade_invalida');
  }

  /**
   * Clique em "Gerar": quando o volume e alto (ver gerarLoteTotalConfirmacao)
   * pede confirmacao explicita antes de enviar, porque a operacao nao tem
   * desfazer. Caso contrario envia direto.
   */
  confirmarGerarLote(): void {
    if (this.gerandoLote || !this.gerarLoteValido || this.proximosSelecionados.length === 0) {
      return;
    }
    if (this.gerarLoteTotalParcelas > this.gerarLoteTotalConfirmacao) {
      this.gerarLoteConfirmVolumeVisivel = true;
      return;
    }
    this.enviarGerarLote();
  }

  /** Confirma o volume alto e segue com a geracao. */
  confirmarGerarLoteVolume(): void {
    this.gerarLoteConfirmVolumeVisivel = false;
    this.enviarGerarLote();
  }

  /** Fecha a confirmacao de volume SEM gerar (volta ao dialog de lote). */
  fecharGerarLoteVolume(): void {
    this.gerarLoteConfirmVolumeVisivel = false;
  }

  /** POST da geracao em lote (ja validada e, quando exigida, confirmada). */
  private enviarGerarLote(): void {
    if (this.gerandoLote || !this.gerarLoteValido || this.proximosSelecionados.length === 0) {
      return;
    }
    this.gerandoLote = true;

    const body: GerarParcelasLoteDTO = {
      contratoIds: this.proximosSelecionados
        .map((c) => c.id)
        .filter((id): id is number => id != null),
      quantidade: this.gerarLoteQuantidade as number,
      dataPrimeiroVencimento: this.formatarDataParaBackend(this.gerarLotePrimeiroVencimento),
    };

    this.contadorService.gerarParcelasLote(body).subscribe({
      next: (res) => {
        this.gerandoLote = false;
        this.gerarLoteDialogVisivel = false;
        this.notificarResultadoGerarLote(res);
        // Recarrega a lista (limpa a selecao dentro de carregarProximos).
        const page = this.proximosRows > 0 ? Math.floor(this.proximosFirst / this.proximosRows) : 0;
        this.carregarProximos(page);
      },
      error: (err) => {
        this.gerandoLote = false;
        this.notificarErroGerarLote(err);
      },
    });
  }

  /**
   * Erro do gerar-lote. Num 400 de validacao o backend devolve a explicacao
   * util em errors[].mensagem (ex.: o teto de parcelas, ja traduzido pelo
   * backend); o campo `message` traz apenas o texto generico de "campos
   * obrigatorios", que nao ajuda o usuario. Preferir a mensagem especifica —
   * mesmo padrao de indicacoes e lista-de-fornecedores.
   * Mantido mesmo com o [max] no template: o atributo pode sumir num refactor e
   * existem clientes HTTP fora do app.
   */
  private notificarErroGerarLote(err: any): void {
    const especifica = err?.error?.errors?.[0]?.mensagem;
    if (err?.status === 400 && especifica) {
      this.messageService.add({ severity: 'error', summary: especifica });
      return;
    }
    this.notificarErro(err);
  }

  /**
   * Toast de resumo do gerar-lote: sucesso com total processado + parcelas
   * geradas; alerta (warn) adicional listando os contratos ignorados (motivos).
   */
  private notificarResultadoGerarLote(res: GerarParcelasLoteResultadoDTO): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('contador.dashboard.proximos.gerar_lote_sucesso', {
        parcelas: res.totalParcelasGeradas,
        contratos: res.totalProcessados,
      }),
    });
    if (res.ignorados && res.ignorados.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.dashboard.proximos.gerar_lote_ignorados', {
          ignorados: res.ignorados.length,
        }),
        detail: res.ignorados
          .map((ig) => `${this.nomeContratoIgnorado(ig.contratoId)}: ${ig.motivo}`)
          .join('\n'),
        life: 8000,
      });
    }
  }

  /** Nome do cliente do contrato ignorado (a partir da lista carregada) para o detalhe. */
  private nomeContratoIgnorado(contratoId: number): string {
    const c = this.proximos.find((x) => x.id === contratoId);
    return c?.nomeEmpresa || `#${contratoId}`;
  }

  // ===========================================================================
  // Novo contrato (solicitar)
  // ===========================================================================

  /** Opcoes do dia de cobranca (1 a 31). */
  readonly diasCobrancaOptions: { label: string; value: number }[] = Array.from(
    { length: 31 },
    (_, i) => ({ label: String(i + 1), value: i + 1 }),
  );

  /** Valores possiveis do mes de referencia (ordem exibida no dropdown). */
  private static readonly MESES_REFERENCIA: MesReferencia[] = [
    'MES_ANTERIOR',
    'MES_ATUAL',
    'PROXIMO_MES',
  ];

  /** Opcoes do dropdown "Mes de referencia" (rotulos localizados; getter =>
   *  reconstrói automaticamente ao trocar o idioma, sem subscription). */
  get mesReferenciaOptions(): { label: string; value: MesReferencia }[] {
    return AreaContadorComponent.MESES_REFERENCIA.map((m) => ({
      value: m,
      label: this.translate.instant(
        `contador_operar.despesa_acessoria.mes_referencia_valores.${m.toLowerCase()}`,
      ),
    }));
  }

  private contratoVazio(): {
    configuracaoEmpresaId: number | null;
    email: string | null;
    cupomId: number | null;
    valorMensal: number | null;
    moeda: MoedaContrato | null;
    diaCobranca: number | null;
    dataVencimento: Date | null;
    mesReferencia: MesReferencia;
    parcelasPorMes: number | null;
    // Criar a conta do cliente (primeiro cadastro) — contrato nasce ATIVO com autonomia.
    criarConta: boolean;
    nomeCliente: string | null;
    /** Pais/DDI do telefone do cliente novo — alimenta o <app-phone-input> (mesmo padrao do /signup). */
    paisTelefone: PaisDDI;
    /** Telefone SEM mascara: o <app-phone-input> emite apenas digitos. */
    telefone: string | undefined;
    nomeEmpresa: string | null;
    ruc: string | null;
    /** Atividade da empresa — NOME do enum (ex.: "ADVOGADOS"), igual ao /signup. */
    atividadeDaEmpresa: string | null;
    /** Id da cidade da empresa. */
    cidadeId: number | null;
    /** Moeda padrao da EMPRESA do cliente — NAO a `moeda` acima, que e a do CONTRATO. */
    moedaEmpresa: string | null;
  } {
    return {
      configuracaoEmpresaId: null,
      email: null,
      cupomId: null,
      valorMensal: null,
      // Contrato do contador e sempre em Guarani (fallback do configMoeda tambem).
      moeda: 'GUARANI',
      diaCobranca: null,
      dataVencimento: null,
      // Default do backend/formulário.
      mesReferencia: 'MES_ATUAL',
      // Quantas parcelas gerar por mês no aceite. Default 1 (comportamento atual).
      parcelasPorMes: 1,
      criarConta: false,
      nomeCliente: null,
      // Paraguai (+595) como padrao, igual ao /signup.
      paisTelefone: PAISES_DDI[0],
      telefone: undefined,
      nomeEmpresa: null,
      ruc: null,
      atividadeDaEmpresa: null,
      cidadeId: null,
      // Default Guarani: 275 das 277 empresas da base operam nessa moeda. Continua visivel
      // e trocavel, com o tooltip avisando que a escolha e definitiva.
      moedaEmpresa: MoedaEnum.GUARANI,
    };
  }

  /** Formata um Date para 'yyyy-MM-dd' (LocalDate do backend); null se vazio. */
  private formatarDataIso(data: Date | null): string | null {
    if (!data) {
      return null;
    }
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  /** Abre o dialog de novo contrato e carrega clientes elegiveis + cupons liberados. */
  abrirNovoContrato(): void {
    this.novoContrato = this.contratoVazio();
    this.erroNovoContrato = null;
    this.limparEstadoBuscaEmail();
    // Todo contrato comeca por "quem e o cliente".
    this.etapaNovoContrato = 1;
    this.dialogNovoContrato = true;
    this.carregarOpcoesContrato();
    // Opcoes do bloco "criar cadastro" (so busca o que ainda nao esta em memoria).
    this.carregarAtividadesEmpresa();
    this.carregarCidades();
  }

  /**
   * Opcoes de "Atividade da empresa" — mesma fonte do /signup
   * (GET /enums/atividadeDaEmpresa: { enum, nome, id }).
   *
   * O `enum` e o valor enviado ao backend; o `nome` ja vem traduzido e e o rotulo.
   * Lista global e estavel: fica em memoria para nao repetir a chamada a cada
   * abertura do modal — mesma ideia das flags `...CarregouUmaVez` das abas. Como
   * os rotulos sao traduzidos pelo backend, recarrega se o idioma tiver mudado.
   */
  carregarAtividadesEmpresa(): void {
    const idiomaAtual = this.translate.currentLang;
    if (this.atividadesEmpresa.length > 0 && this.atividadesIdioma === idiomaAtual) {
      return;
    }
    this.carregandoAtividades = true;
    this.erroAtividades = false;
    this.atividadeEmpresaService.listarAtividades().subscribe({
      next: (res) => {
        const lista: AtividadeBackend[] = Array.isArray(res) ? res : (res?.content ?? []);
        this.atividadesEmpresa = (lista || [])
          .map((item) => ({ label: item.nome, value: item.enum }))
          .sort((a, b) => a.label.localeCompare(b.label));
        this.atividadesIdioma = idiomaAtual;
        this.carregandoAtividades = false;
      },
      error: () => {
        this.atividadesEmpresa = [];
        this.atividadesIdioma = null;
        this.erroAtividades = true;
        this.carregandoAtividades = false;
      },
    });
  }

  /**
   * Opcoes de "Cidade" — mesma fonte do /signup (GET /cidade?listarTodos=true,
   * `Page.content`), ordenadas por nome. Tambem fica em memoria entre aberturas
   * do modal (lista global, nao depende do idioma).
   */
  carregarCidades(): void {
    if (this.cidades.length > 0) {
      return;
    }
    this.carregandoCidades = true;
    this.erroCidades = false;
    this.cidadeService.get('/cidade', { listarTodos: true }).subscribe({
      next: (res: CidadeResponseDTO) => {
        const lista: CidadeDTO[] = res?.content ?? [];
        this.cidades = lista
          .map((c: CidadeDTO) => ({
            label: c.nome,
            value: Number(c.id),
            nomeEstado: c.nomeEstado ?? '',
          }))
          .sort((a: CidadeOption, b: CidadeOption) => a.label.localeCompare(b.label));
        this.carregandoCidades = false;
      },
      error: () => {
        this.cidades = [];
        this.erroCidades = true;
        this.carregandoCidades = false;
      },
    });
  }

  private carregarOpcoesContrato(): void {
    this.carregandoOpcoesContrato = true;
    this.clientesDisponiveis = [];
    this.cuponsDisponiveis = [];
    this.cuponsCarregados = [];

    this.contadorService.listarClientesDisponiveis().subscribe({
      next: (res) => {
        this.clientesDisponiveis = res || [];
      },
      error: (err) => this.notificarErro(err),
    });

    this.contadorService.listarCuponsLiberados().subscribe({
      next: (res) => {
        this.cuponsCarregados = res || [];
        this.cuponsDisponiveis = this.cuponsCarregados.map((c) => ({
          label: this.montarLabelCupom(c),
          value: c.id,
        }));
        this.carregandoOpcoesContrato = false;
      },
      error: (err) => {
        this.carregandoOpcoesContrato = false;
        this.notificarErro(err);
      },
    });
  }

  /** Label do cupom: "10% desc. · 5% comissão (NOME)". */
  private montarLabelCupom(cupom: CupomContadorConsultaDTO): string {
    const desc = this.translate.instant('contador.dashboard.novo_contrato.cupom_desconto_abrev');
    const comissao = this.translate.instant('contador.dashboard.novo_contrato.cupom_comissao_abrev');
    return `${cupom.porcentagemDesconto}% ${desc} · ${cupom.porcentagemComissao}% ${comissao} (${cupom.nome})`;
  }

  // ===========================================================================
  // Secao "Desconto e comissao" do modal Novo contrato
  //
  // Os dois percentuais do cupom ja estao em memoria (`cuponsCarregados`, carregado
  // com o modal): a secao nao faz nenhuma chamada nova.
  // ===========================================================================

  /** Cupom escolhido no dropdown, ou `null` enquanto o contador nao escolheu. */
  get cupomSelecionado(): CupomContadorConsultaDTO | null {
    const id = this.novoContrato.cupomId;
    if (id == null) {
      return null;
    }
    return this.cuponsCarregados.find((cupom) => cupom.id === id) ?? null;
  }

  /** Percentual de DESCONTO do cupom escolhido (do cliente); '' sem cupom. */
  get percentualDescontoCupom(): string {
    const cupom = this.cupomSelecionado;
    return cupom ? `${cupom.porcentagemDesconto}` : '';
  }

  /** Percentual de COMISSAO do cupom escolhido (do contador) — campo DIFERENTE do
   *  desconto; '' sem cupom. */
  get percentualComissaoCupom(): string {
    const cupom = this.cupomSelecionado;
    return cupom ? `${cupom.porcentagemComissao}` : '';
  }

  /** Linha do desconto: versao COM o percentual quando ha cupom, generica sem ele. */
  get chaveTextoDescontoCupom(): string {
    return this.cupomSelecionado
      ? 'contador.dashboard.novo_contrato.cupom_explica_desconto_valor'
      : 'contador.dashboard.novo_contrato.cupom_explica_desconto';
  }

  /** Linha da comissao: versao COM o percentual quando ha cupom, generica sem ele. */
  get chaveTextoComissaoCupom(): string {
    return this.cupomSelecionado
      ? 'contador.dashboard.novo_contrato.cupom_explica_comissao_valor'
      : 'contador.dashboard.novo_contrato.cupom_explica_comissao';
  }

  // ===========================================================================
  // Navegacao entre as etapas do modal Novo contrato
  // ===========================================================================

  /**
   * Ha uma checagem de email por vir (no debounce) ou em voo?
   *
   * Nao basta olhar `buscandoEmpresas`: ele so liga depois dos 400ms do debounce, e o clique
   * em "Continuar" costuma acontecer exatamente nessa janela.
   */
  get checagemEmailPendente(): boolean {
    if (this.buscandoEmpresas) {
      return true;
    }
    const email = this.emailNormalizadoAtual;
    // Fragmento nunca chega a ser consultado: nao ha o que esperar.
    if (!isEmailValido(email)) {
      return false;
    }
    return this.ultimoEmailChecado !== email;
  }

  /** O "Continuar" espera a checagem de email terminar (vira spinner enquanto isso). */
  get aguardandoChecagemParaAvancar(): boolean {
    return this.avancoPendente && this.checagemEmailPendente;
  }

  /**
   * Avanca uma etapa.
   *
   * ⚠️ A checagem de email so segura a SAIDA DA ETAPA 1 — e la que o email mora e que a
   * resposta muda o formulario (auto-marcar "criar cadastro" ou bloquear). Nas etapas 2 e 3
   * nao ha nada em voo para esperar, e travar o botao ali seria atraso sem motivo.
   */
  avancarEtapa(): void {
    if (this.etapaNovoContrato === 1 && this.checagemEmailPendente) {
      this.avancoPendente = true;
      return;
    }
    this.avancoPendente = false;
    if (!this.etapaAtualValida || this.etapaNovoContrato === 3) {
      return;
    }
    this.irParaEtapa((this.etapaNovoContrato + 1) as 1 | 2 | 3);
  }

  /** Volta uma etapa. Nada e limpo: o formulario e um objeto so. */
  voltarEtapa(): void {
    this.avancoPendente = false;
    if (this.etapaNovoContrato === 1) {
      return;
    }
    this.irParaEtapa((this.etapaNovoContrato - 1) as 1 | 2 | 3);
  }

  /**
   * Decide o avanco que ficou pendente, agora com a resposta da checagem em maos.
   *
   * Permanece na etapa 1 sempre que a resposta mudou o que ela pede — auto-marcou o
   * "criar cadastro" (7 campos novos) ou bloqueou o email. O aviso correspondente ja esta
   * na tela e explica por que nao avancou.
   */
  private resolverAvancoPendente(): void {
    if (!this.avancoPendente || this.checagemEmailPendente) {
      return;
    }
    this.avancoPendente = false;
    // O avanco pendente so existe na etapa 1 (unica que espera a checagem).
    if (this.etapaNovoContrato !== 1 || !this.etapaClienteValida) {
      return;
    }
    this.irParaEtapa(2);
  }

  /** Troca de etapa e devolve o foco ao titulo (teclado e leitor de tela). */
  private irParaEtapa(etapa: 1 | 2 | 3): void {
    this.etapaNovoContrato = etapa;
    // A troca de etapa recria o conteudo (ngIf): o foco so existe no proximo ciclo.
    setTimeout(() => this.tituloEtapaRef?.nativeElement?.focus(), 0);
  }

  /** Chave i18n do titulo da etapa aberta (alvo do foco a cada troca). */
  get chaveTituloEtapa(): string {
    if (this.etapaNovoContrato === 1) {
      return 'contador.dashboard.novo_contrato.etapa_cliente_titulo';
    }
    if (this.etapaNovoContrato === 2) {
      return 'contador.dashboard.novo_contrato.etapa_contrato_titulo';
    }
    return 'contador.dashboard.novo_contrato.etapa_cupom_titulo';
  }

  /** Resumo do cliente, exibido nas etapas 2 e 3 (o wizard tira a etapa 1 de vista). */
  get resumoCliente(): string {
    const c = this.novoContrato;
    const email = c.email?.trim();
    if (email) {
      return email;
    }
    const cliente = this.clientesDisponiveis.find(
      (item) => item.configuracaoEmpresaId === c.configuracaoEmpresaId,
    );
    return cliente?.nome || '';
  }

  fecharNovoContrato(): void {
    this.dialogNovoContrato = false;
  }

  /** Config de formatacao (simbolo/casas decimais/locale) da moeda selecionada — do enum Moeda. */
  get configMoeda(): MoedaConfig {
    const m = (this.novoContrato.moeda as string) || MoedaEnum.GUARANI;
    return MOEDA_CONFIG[m as MoedaEnum] ?? MOEDA_CONFIG[MoedaEnum.GUARANI];
  }

  /**
   * Config de formatacao da moeda de um contrato LISTADO (colunas "Valor mensal").
   * O contrato do contador e sempre em Guarani, mas o DTO carrega a moeda —
   * respeitamos o campo com fallback GUARANI. Mesmo padrao do card "Contrato
   * ativo" (despesa-acessoria), que formata valorMensal via | moedaSimbolo.
   */
  configMoedaContrato(contrato: ContratoContadorConsultaDTO): MoedaConfig {
    const m = (contrato.moeda?.enum as string) || MoedaEnum.GUARANI;
    return MOEDA_CONFIG[m as MoedaEnum] ?? MOEDA_CONFIG[MoedaEnum.GUARANI];
  }

  /** Casas decimais da moeda selecionada (deriva do formato '1.2-2' -> 2 / '1.0-0' -> 0). */
  get casasDecimaisMoeda(): number {
    return Number(this.configMoeda.digits.split('-')[1] ?? 0);
  }

  /**
   * Glifos de moeda para o prefixo do campo "Valor mensal". Iguais aos usados em
   * operar-despesa-acessoria/produto-selecionar (consistencia no projeto). Note que
   * configMoeda.symbol e o codigo ISO (PYG/BRL/...), por isso o mapa proprio.
   * O contrato do contador e sempre em Guarani, mas o mapa cobre as demais moedas
   * caso a origem da moeda mude no futuro.
   */
  private static readonly SIMBOLOS_MOEDA: Record<string, string> = {
    GUARANI: 'Gs',
    REAL: 'R$',
    DOLAR: '$',
    PESO: '$',
  };

  /** Prefixo (glifo + espaco) exibido dentro do p-inputNumber do campo "Valor mensal". */
  get prefixoMoeda(): string {
    const m = (this.novoContrato.moeda as string) || MoedaEnum.GUARANI;
    return `${AreaContadorComponent.SIMBOLOS_MOEDA[m] ?? ''} `;
  }

  /** Chave i18n do nome da moeda do contrato (ex.: enums.moeda.guarani), para o label. */
  get moedaNomeChave(): string {
    const m = ((this.novoContrato.moeda as string) || MoedaEnum.GUARANI).toLowerCase();
    return `enums.moeda.${m}`;
  }

  /** Verdadeiro quando o campo email esta preenchido, porem com formato invalido.
   *  Campo e opcional (cliente pode vir do dropdown); so validamos o FORMATO quando digitado. */
  get emailFormatoInvalido(): boolean {
    const email = this.novoContrato.email?.trim();
    return !!email && !isEmailValido(email);
  }

  /** Dispara a checagem de conta existente pelo email digitado (somente email completo consulta). */
  buscarEmpresasExistentes(email: string | null | undefined): void {
    // Prefixo de sessão: ver `sessaoBuscaEmail`.
    this.buscaEmpresaSubject$.next(`${this.sessaoBuscaEmail}|${(email || '').trim()}`);
  }

  /**
   * Zera tudo que a checagem por email deixou para trás. Chamado ao abrir o modal: sem isso
   * o aviso e as trilhas de marcação do contrato anterior reapareceriam no formulário novo.
   */
  private limparEstadoBuscaEmail(): void {
    this.empresasEncontradas = [];
    this.buscandoEmpresas = false;
    this.erroBuscaEmail = false;
    this.emailSemConta = null;
    this.criarContaAutoMarcadoParaEmail = null;
    this.emailsComDesmarqueManual.clear();
    this.autoMarcacaoLiberada = false;
    this.avancoPendente = false;
    this.ultimoEmailChecado = null;
    // Nova sessão: o mesmo email digitado de novo volta a ser consultado (ver `sessaoBuscaEmail`).
    this.sessaoBuscaEmail++;
  }

  /** Email do formulário normalizado (trim + lowercase), ou '' — chave das trilhas de marcação. */
  private get emailNormalizadoAtual(): string {
    return (this.novoContrato.email || '').trim().toLowerCase();
  }

  /** Checa a situação da conta a cada mudança do email, marcando ou não o "criar cadastro". */
  onEmailContratoChange(): void {
    // Voltou a digitar: o valor ainda não é final, então a auto-marcação fica suspensa até o
    // blur. Sem isso, "cliente@gmail.c" (que a regex aceita) marcaria o checkbox no meio da
    // digitação e os 8 campos do cadastro apareceriam e sumiriam a cada tecla.
    this.autoMarcacaoLiberada = false;
    this.buscarEmpresasExistentes(this.novoContrato.email);
  }

  /**
   * Saída do campo email: o valor está estável, então esta é a hora de auto-marcar.
   *
   * Reemite o email para cobrir o caso de o contador sair do campo antes de o debounce
   * disparar. Se o valor não mudou, o `distinctUntilChanged` descarta a emissão — e está
   * certo: a resposta anterior continua válida e `aplicarAutoMarcacaoCriarConta()` decide
   * com ela na mesma hora.
   */
  onEmailContratoBlur(): void {
    this.autoMarcacaoLiberada = true;
    this.buscarEmpresasExistentes(this.novoContrato.email);
    this.aplicarAutoMarcacaoCriarConta();
  }

  /** Toggle "criar cadastro": ao ligar, desliga o dropdown de indicados.
   *  Nunca busca pelo nome do cliente — isso expunha dados de contas de terceiros. */
  onToggleCriarConta(): void {
    // Só o clique do contador chega aqui: o `(onChange)` do p-checkbox não dispara quando o
    // código altera `novoContrato.criarConta`. Logo, isto é sempre intenção MANUAL, e ela
    // vence a automática — a partir daqui o estado do checkbox deixa de ser "nosso".
    const email = this.emailNormalizadoAtual;
    this.criarContaAutoMarcadoParaEmail = null;

    if (this.novoContrato.criarConta) {
      // Criar conta não usa o dropdown de indicados.
      this.novoContrato.configuracaoEmpresaId = null;
      // Marcou à mão: um desmarque anterior deste email não deve mais bloquear nada.
      if (email) {
        this.emailsComDesmarqueManual.delete(email);
      }
    } else if (email) {
      // Desmarcou à mão: não re-marcar este email (ele prefere o convite).
      this.emailsComDesmarqueManual.add(email);
    }

    // Revalida sem limpar `empresasEncontradas` antes: o `distinctUntilChanged` do pipeline
    // descarta o email repetido, então limpar aqui derrubava o aviso para sempre.
    if (email) {
      this.buscarEmpresasExistentes(email);
    } else {
      this.empresasEncontradas = [];
    }
  }

  /**
   * Marca o "criar cadastro" quando o email digitado comprovadamente não tem conta.
   *
   * Todas as guardas são de abstenção: na dúvida, não mexe no formulário. Só age com
   * (a) valor estável (blur), (b) checkbox ainda desmarcado, (c) nenhuma resposta em voo,
   * (d) sem erro na checagem, (e) 200 + lista vazia para ESTE email e (f) sem desmarque
   * manual anterior para ele.
   */
  private aplicarAutoMarcacaoCriarConta(): void {
    if (!this.autoMarcacaoLiberada || this.novoContrato.criarConta) {
      return;
    }
    if (this.buscandoEmpresas || this.erroBuscaEmail) {
      return;
    }
    const email = this.emailNormalizadoAtual;
    // `emailSemConta` é do email respondido: se o campo já mudou, a resposta não vale mais.
    if (!email || this.emailSemConta !== email) {
      return;
    }
    if (this.emailsComDesmarqueManual.has(email)) {
      return;
    }
    this.novoContrato.criarConta = true;
    this.criarContaAutoMarcadoParaEmail = email;
    // Mesmo efeito colateral do toggle manual: criar cadastro não usa o dropdown de indicados.
    this.novoContrato.configuracaoEmpresaId = null;
  }

  /**
   * Desfaz a auto-marcação quando ela deixa de valer (o email mudou, ou passou a ter conta —
   * típico do typo corrigido).
   *
   * Desfaz APENAS o que o sistema marcou: se `criarContaAutoMarcadoParaEmail` é null, o
   * checkbox reflete escolha do contador e não se toca nele.
   */
  private revisarAutoMarcacaoCriarConta(): void {
    if (this.criarContaAutoMarcadoParaEmail === null) {
      return;
    }
    const email = this.emailNormalizadoAtual;
    // Segue valendo: mesmo email e ele continua sem conta.
    if (this.emailSemConta === email && this.criarContaAutoMarcadoParaEmail === email) {
      return;
    }
    this.novoContrato.criarConta = false;
    this.criarContaAutoMarcadoParaEmail = null;
  }

  /**
   * Verdadeiro quando o "criar cadastro" atual foi marcado PELO SISTEMA — habilita o aviso
   * que explica por que o formulário mudou sozinho e como reverter.
   */
  get criarContaFoiAutoMarcado(): boolean {
    return (
      this.novoContrato.criarConta &&
      this.criarContaAutoMarcadoParaEmail !== null &&
      this.criarContaAutoMarcadoParaEmail === this.emailNormalizadoAtual
    );
  }

  /**
   * Filtra o RUC digitado no modal "Novo contrato" (formato "NNNNNNN-D").
   * Mesma regra do /signup — centralizada em shared/utils/ruc.util.ts.
   */
  filtrarRucNovoContrato(event: Event): void {
    this.novoContrato.ruc = filtrarRucDoEvento(event);
  }

  /**
   * Normaliza a `situacao` devolvida pelo backend para um dos 4 estados que a tela sabe
   * desenhar. Degradacao graciosa (o backend pode ser mais antigo ou mais novo que a tela):
   * - ausente/vazia  -> DISPONIVEL  (backend anterior ao campo; mantem o comportamento antigo)
   * - valor conhecido -> ele mesmo
   * - valor novo/desconhecido -> DESCONHECIDA (avisa que a conta existe, mas nao oferece
   *   "usar esta conta" nem bloqueia, porque a tela nao conhece a semantica do valor)
   */
  situacaoConta(conta: ContaExistenteContratoDTO): SituacaoContaExibida {
    const situacao = (conta?.situacao || '').toString().trim().toUpperCase();
    if (!situacao || situacao === 'DISPONIVEL') {
      return 'DISPONIVEL';
    }
    if (situacao === 'JA_TEM_CONTRATO') {
      return 'JA_TEM_CONTRATO';
    }
    if (situacao === 'PROPRIA_EMPRESA') {
      return 'PROPRIA_EMPRESA';
    }
    if (situacao === 'DE_OUTRO_CONTADOR') {
      return 'DE_OUTRO_CONTADOR';
    }
    return 'DESCONHECIDA';
  }

  /**
   * Verdadeiro quando a conta do email torna o contrato um erro certo: JA_TEM_CONTRATO,
   * PROPRIA_EMPRESA ou DE_OUTRO_CONTADOR. Desabilita "Criar contrato" via `novoContratoValido`.
   * DESCONHECIDA não bloqueia — só avisa.
   *
   * Independe do checkbox "criar cadastro": os três impedimentos valem para o modal inteiro.
   */
  get contaExistenteBloqueia(): boolean {
    return this.empresasEncontradas.some((conta) => {
      const situacao = this.situacaoConta(conta);
      return (
        situacao === 'JA_TEM_CONTRATO' ||
        situacao === 'PROPRIA_EMPRESA' ||
        situacao === 'DE_OUTRO_CONTADOR'
      );
    });
  }

  /**
   * Campos obrigatorios ainda vazios, ja localizados, na ORDEM em que aparecem no modal.
   *
   * O modal tem scroll interno e Cupon/Valor/Dia/Vencimiento ficam abaixo da dobra: sem esta
   * lista o contador via so um botao apagado e concluia que "nao acontece nada". Espelha
   * `novoContratoValido` — mexeu em um, mexa no outro.
   */
  get camposFaltantesNovoContrato(): string[] {
    // Situacao do email bloqueia: preencher os campos NAO habilita o botao, entao listar o
    // que falta prometeria um resultado impossivel. Quem explica o impedimento e o aviso da
    // situacao, que ja esta na tela.
    if (this.contaExistenteBloqueia) {
      return [];
    }
    return [
      ...this.camposFaltantesEtapaCliente,
      ...this.camposFaltantesEtapaContrato,
      ...this.camposFaltantesEtapaCupom,
    ];
  }

  /**
   * Pendencias da ETAPA 1 (quem e o cliente).
   *
   * O curto-circuito do bloqueio vale SO nesta etapa: e aqui que o email — a causa do
   * impedimento — pode ser corrigido. Na etapa 2 as pendencias continuam sendo listadas.
   */
  get camposFaltantesEtapaCliente(): string[] {
    if (this.contaExistenteBloqueia) {
      return [];
    }
    const c = this.novoContrato;
    const faltantes: string[] = [];
    // Reusa os MESMOS rotulos dos campos: o texto do aviso tem de bater com o que esta
    // escrito na tela, senao o contador procura um campo que nao existe com aquele nome.
    const rotulo = (chave: string) => this.translate.instant(`contador.dashboard.${chave}`);

    if (c.criarConta) {
      if (!(c.email && c.email.trim())) faltantes.push(rotulo('novo_contrato.email'));
      if (!(c.nomeCliente && c.nomeCliente.trim())) {
        faltantes.push(rotulo('novo_contrato.nombre_cliente'));
      }
      if (!(c.paisTelefone && c.paisTelefone.ddi) || !(c.telefone && c.telefone.trim())) {
        faltantes.push(rotulo('novo_contrato.telefono'));
      }
      if (!(c.ruc && c.ruc.trim())) faltantes.push(rotulo('novo_contrato.ruc'));
      if (!c.atividadeDaEmpresa) faltantes.push(rotulo('novo_contrato.atividade'));
      if (c.cidadeId == null) faltantes.push(rotulo('novo_contrato.cidade'));
      if (!c.moedaEmpresa) faltantes.push(rotulo('novo_contrato.moeda_empresa'));
    } else if (c.configuracaoEmpresaId == null && !(c.email && c.email.trim())) {
      faltantes.push(rotulo('novo_contrato.cliente'));
    }
    return faltantes;
  }

  /** Pendencias da ETAPA 2 (termos da cobranca). */
  get camposFaltantesEtapaContrato(): string[] {
    const c = this.novoContrato;
    const faltantes: string[] = [];
    const rotulo = (chave: string) => this.translate.instant(`contador.dashboard.${chave}`);

    if (c.valorMensal == null || c.valorMensal <= 0) faltantes.push(rotulo('tabela.valor_mensal'));
    if (c.diaCobranca == null) faltantes.push(rotulo('novo_contrato.dia_cobranca'));
    if (c.dataVencimento == null) faltantes.push(rotulo('novo_contrato.data_vencimento'));

    return faltantes;
  }

  /** Pendencias da ETAPA 3 (desconto e comissao) — so o cupom. */
  get camposFaltantesEtapaCupom(): string[] {
    const rotulo = (chave: string) => this.translate.instant(`contador.dashboard.${chave}`);
    return this.novoContrato.cupomId == null ? [rotulo('novo_contrato.cupom')] : [];
  }

  /** Pendencias da etapa ABERTA — o aviso so cobra o que da para resolver agora. */
  get camposFaltantesEtapaAtual(): string[] {
    if (this.etapaNovoContrato === 1) {
      return this.camposFaltantesEtapaCliente;
    }
    if (this.etapaNovoContrato === 2) {
      return this.camposFaltantesEtapaContrato;
    }
    return this.camposFaltantesEtapaCupom;
  }

  /**
   * ETAPA 1 completa? Habilita o "Continuar".
   *
   * O bloqueio por situacao do email entra aqui (e nao so no salvar): com ele nao existe
   * contrato possivel em modo nenhum, entao avancar so adiaria a descoberta.
   */
  get etapaClienteValida(): boolean {
    const c = this.novoContrato;
    if (this.contaExistenteBloqueia) {
      return false;
    }
    // Criar conta: exige os dados minimos da empresa nova (mesmos do /signup).
    // RUC, atividade, cidade e moeda padrao sao obrigatorios no backend (422) — bloqueamos
    // aqui para o contador nunca chegar no erro.
    if (c.criarConta) {
      return (
        !!(c.email && c.email.trim()) &&
        !!(c.nomeCliente && c.nomeCliente.trim()) &&
        !!(c.paisTelefone && c.paisTelefone.ddi) &&
        !!(c.telefone && c.telefone.trim()) &&
        !!(c.ruc && c.ruc.trim()) &&
        !!c.atividadeDaEmpresa &&
        c.cidadeId != null &&
        !!c.moedaEmpresa
      );
    }
    // Caso normal: cliente do dropdown OU email.
    return c.configuracaoEmpresaId != null || !!(c.email && c.email.trim());
  }

  /** ETAPA 2 completa? Termos sem os quais o backend nao gera os titulos mensais. */
  get etapaContratoValida(): boolean {
    const c = this.novoContrato;
    return (
      c.valorMensal != null &&
      c.valorMensal > 0 &&
      c.diaCobranca != null &&
      c.dataVencimento != null
    );
  }

  /** ETAPA 3 completa? O cupom e obrigatorio: dele saem o desconto e a comissao. */
  get etapaCupomValida(): boolean {
    return this.novoContrato.cupomId != null;
  }

  /** Validacao da etapa ABERTA — habilita o "Continuar" de cada passo. */
  get etapaAtualValida(): boolean {
    if (this.etapaNovoContrato === 1) {
      return this.etapaClienteValida;
    }
    if (this.etapaNovoContrato === 2) {
      return this.etapaContratoValida;
    }
    return this.etapaCupomValida;
  }

  /**
   * Validacao minima do formulario (habilita o botao salvar).
   *
   * ⚠️ Semantica IDENTICA a de antes do wizard — a divisao em etapas so REPARTIU as mesmas
   * checagens. Se um dia deixar de ser a conjuncao das tres, o modal passa a permitir salvar
   * algo que uma das etapas considera incompleto.
   */
  get novoContratoValido(): boolean {
    return this.etapaClienteValida && this.etapaContratoValida && this.etapaCupomValida;
  }

  salvarContrato(): void {
    if (!this.novoContratoValido || this.salvandoContrato) {
      return;
    }
    // Nova tentativa limpa o erro anterior: manter o texto antigo na tela enquanto a
    // requisicao roda faria o contador ler um erro que ja nao vale.
    this.erroNovoContrato = null;
    // Valida o FORMATO do email apenas no submit (campo continua opcional).
    if (this.emailFormatoInvalido) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('contador.dashboard.novo_contrato.email_invalido'),
      });
      return;
    }
    const c = this.novoContrato;
    const email = c.email?.trim() || null;
    // Telefone apenas com digitos (a mascara do <app-phone-input> e visual),
    // mesmo tratamento do /signup e do cadastro de clientes.
    const telefoneLimpo = c.telefone?.replace(/\D/g, '') || null;
    const dto: SolicitarContratoContadorDTO = {
      // Cliente pelo dropdown OU pelo email (email/criarConta tem precedencia).
      configuracaoEmpresaId: email || c.criarConta ? null : c.configuracaoEmpresaId,
      email: email,
      cupomId: c.cupomId!,
      valorMensal: c.valorMensal!,
      // Contrato do contador e sempre em Guarani.
      moeda: 'GUARANI',
      diaCobranca: c.diaCobranca,
      dataVencimento: this.formatarDataIso(c.dataVencimento),
      mesReferencia: c.mesReferencia,
      // Default 1 quando o campo estiver vazio (nunca envia null/0).
      parcelasPorMes: c.parcelasPorMes && c.parcelasPorMes >= 1 ? c.parcelasPorMes : 1,
      // Criar conta do cliente (senha gerada + email; contrato nasce ATIVO com autonomia).
      criarConta: c.criarConta,
      nomeCliente: c.criarConta ? c.nomeCliente?.trim() || null : null,
      // Formato do DDI preservado ("+595"), igual ao que ja era enviado antes.
      ddi: c.criarConta ? c.paisTelefone?.ddi?.trim() || null : null,
      telefone: c.criarConta ? telefoneLimpo : null,
      nomeEmpresa: c.criarConta ? c.nomeEmpresa?.trim() || null : null,
      ruc: c.criarConta ? c.ruc?.trim() || null : null,
      // NOME do enum (ex.: "ADVOGADOS"), igual ao /signup — nao o id.
      atividadeDaEmpresa: c.criarConta ? c.atividadeDaEmpresa : null,
      cidadeId: c.criarConta && c.cidadeId != null ? Number(c.cidadeId) : null,
      // Da EMPRESA (NOME do enum). A do contrato vai em `moeda`, acima, sempre GUARANI.
      moedaEmpresa: c.criarConta ? c.moedaEmpresa : null,
    };
    this.salvandoContrato = true;
    this.contadorService.solicitarContrato(dto).subscribe({
      next: (res) => {
        this.salvandoContrato = false;
        this.dialogNovoContrato = false;
        if (res?.tipo === 'CONVITE') {
          // Cliente sem conta: o backend criou o convite -> popup informando que precisa criar conta.
          this.conviteMensagem =
            res.mensagem || this.translate.instant('contador.dashboard.novo_contrato.convite_texto');
          this.dialogConviteVisivel = true;
        } else if (c.criarConta && res?.contrato?.status === 'ATIVO' && email) {
          // Conta criada AGORA (so este fluxo devolve contrato ja ATIVO). Mensagem montada
          // aqui, e nao `res.mensagem`, porque a do backend nao diz PARA QUAL email a senha
          // foi enviada — que e a duvida imediata do contador.
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('contador.dashboard.novo_contrato.sucesso_conta_criada'),
            detail: this.translate.instant(
              'contador.dashboard.novo_contrato.sucesso_conta_criada_detalhe',
              { email }
            ),
            life: 8000,
          });
        } else {
          // A mensagem vem do backend ja localizada e MUDA conforme o fluxo:
          // contrato normal ("aguarde a aceitacao do cliente") x criar conta
          // ("conta criada e contrato ativado" — aqui nao ha aceite a esperar).
          // Fallback na chave local quando o backend nao mandar mensagem.
          this.messageService.add({
            severity: 'success',
            summary:
              res?.mensagem || this.translate.instant('contador.dashboard.novo_contrato.sucesso'),
          });
        }
        this.recarregarContratos();
        this.carregarResumo();
      },
      error: (err) => {
        this.salvandoContrato = false;
        // Alem do toast: o erro fica FIXO dentro do modal. O toast nasce no canto superior
        // direito, a ~1500px do botao, e some em 3s — quem esta olhando o rodape do modal
        // simplesmente nao ve, e conclui que o clique nao fez nada.
        this.erroNovoContrato = this.mensagemErro(err);
        this.notificarErro(err);
        // A recusa pode ser por causa do CLIENTE (email ja vinculado, dados da conta nova).
        // Nesse caso o contador nao tem o que corrigir na etapa 2: devolvemos ele a etapa 1,
        // onde estao os campos em questao. O erro segue visivel — o bloco fica fora das etapas.
        if (!this.etapaClienteValida) {
          this.irParaEtapa(1);
        }
      },
    });
  }

  // ===========================================================================
  // Helpers de exibicao (tabela / ranking)
  // ===========================================================================

  /** Label localizado do status do contrato. */
  labelStatus(status: StatusContratoContador): string {
    return this.translate.instant(`contador.dashboard.tabela.status.${status.toLowerCase()}`);
  }

  /** Severity (cor) da tag conforme o status do contrato. */
  severityStatus(status: StatusContratoContador): 'success' | 'warning' | 'danger' | 'secondary' {
    switch (status) {
      case 'ATIVO':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'RECUSADO':
        return 'danger';
      default: // CANCELADO, VENCIDO
        return 'secondary';
    }
  }

  // ===========================================================================
  // Erros
  // ===========================================================================

  /** Toast de erro amigavel; nunca vaza mensagem tecnica. */
  private notificarErro(err: any): void {
    this.messageService.add({ severity: 'error', summary: this.mensagemErro(err) });
  }

  private mensagemErro(err: any): string {
    const status = err?.status;
    if (status === 403) {
      // Defensivo: nao deveria ocorrer no branch contador, mas trata sem vazar tecnico.
      return this.translate.instant('contador.dashboard.erros.acesso_negado');
    }
    // 401 e tratado globalmente pelo AuthInterceptor (logout/redirect) — nao tratar aqui.
    return err?.error?.message || this.translate.instant('contador.dashboard.erros.generico');
  }
}
